import { Icon } from '@iconify/react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  fetchJobQuotePdf,
  getQuotesAwaitingApproval,
  QuotationPdfFile,
  QuoteAwaitingApproval,
  setJobQuoteApproval,
} from '../../admin/admin.api';
import { logError } from '../../../utils/logger';
import { Card, SectionTitle } from '../../shared/components';
import useAsyncData from '../../shared/hooks/useAsyncData';
import { formatAmount } from '../../shared/utils/misc';
import { clientTypeLabel } from '../components/ClientTypeField';
import QuotationPdfViewer from '../reports/QuotationPdfViewer';

const formatDate = (value: string | null | undefined) => {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? '—'
    : parsed.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

/**
 * The admin's approval queue (V36): every quotation Accounts has added a work order to and
 * that is not yet approved. An admin's "Quotation awaiting approval" notification opens this
 * page with that quotation picked out (`?quote=`), so it is approved here directly — after a
 * look at its letter, which opens in place.
 */
export default function QuotationApprovalsPage() {
  const [params] = useSearchParams();
  const focusId = Number(params.get('quote')) || null;

  const fetchQueue = useCallback(() => getQuotesAwaitingApproval(), []);
  const { data: rows, loading, error, refetch } = useAsyncData<QuoteAwaitingApproval[]>(fetchQueue, [], {
    code: 'QUOTE_APPROVALS_LOAD_FAILED',
    errorMessage: 'Failed to load the quotations awaiting approval',
  });

  const [approving, setApproving] = useState<number | null>(null);
  const [opening, setOpening] = useState<number | null>(null);
  const [viewing, setViewing] = useState<{ file: QuotationPdfFile; row: QuoteAwaitingApproval } | null>(null);
  /** The blob URL the viewer shows; revoked when replaced or closed, as the quotation list does. */
  const ownedUrl = useRef<string | null>(null);
  const release = () => {
    if (ownedUrl.current) URL.revokeObjectURL(ownedUrl.current);
    ownedUrl.current = null;
  };
  useEffect(() => () => release(), []);

  async function approve(row: QuoteAwaitingApproval) {
    setApproving(row.quoteId);
    try {
      await setJobQuoteApproval(row.quoteId, true);
      toast.success(`Quotation for ${row.jobCode} approved — bills can now be raised from it`);
      refetch();
    } catch (err) {
      toast.error(logError({ message: 'Could not approve the quotation', error: err, code: 'QUOTE_APPROVE_FAILED' }));
    } finally {
      setApproving(null);
    }
  }

  async function view(row: QuoteAwaitingApproval) {
    if (opening !== null) return;
    setOpening(row.quoteId);
    try {
      const file = await fetchJobQuotePdf(row.quoteId, true);
      release();
      ownedUrl.current = file.url;
      setViewing({ file, row });
    } catch (err) {
      toast.error(logError({ message: 'Could not open the quotation PDF', error: err, code: 'QUOTE_PDF_FAILED' }));
    } finally {
      setOpening(null);
    }
  }

  const focused = focusId != null && rows.some((r) => r.quoteId === focusId);

  return (
    <Stack gap={2}>
      <SectionTitle title='Quotation Approvals' />

      {error && (
        <Alert severity='error' action={<Button onClick={refetch}>Retry</Button>}>
          {error}
        </Alert>
      )}
      {focusId != null && !loading && !focused && !error && (
        <Alert severity='info' variant='outlined'>
          That quotation is no longer waiting — it has been approved already, or its work order was removed.
        </Alert>
      )}

      <Card>
        {loading ? (
          <Stack alignItems='center' py={4}>
            <CircularProgress size={28} />
          </Stack>
        ) : rows.length === 0 ? (
          <Typography variant='body2' color='text.secondary' sx={{ p: 2 }}>
            Nothing is waiting. A quotation appears here once Accounts adds the client&apos;s work order to it.
          </Typography>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>Job</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Quotation</TableCell>
                  <TableCell align='right'>Value (ex GST)</TableCell>
                  <TableCell>Work Order</TableCell>
                  <TableCell align='center'>Letter</TableCell>
                  <TableCell align='right'>Approval</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.quoteId} hover selected={row.quoteId === focusId}>
                    <TableCell>
                      <Typography variant='body2' fontWeight={500}>{row.jobCode}</Typography>
                      <Typography variant='caption' color='text.secondary'>{row.jobName}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='body2'>{row.clientName ?? '—'}</Typography>
                      <Typography variant='caption' color='text.secondary'>
                        {clientTypeLabel(row.clientType)}{row.companyName ? ` · ${row.companyName}` : ''}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Stack direction='row' alignItems='center' gap={1}>
                        {formatDate(row.quoteDate)}
                        {!row.current && (
                          <Tooltip title='A newer quotation has been raised for this job since'>
                            <Chip label='Superseded' size='small' variant='outlined' color='warning' />
                          </Tooltip>
                        )}
                      </Stack>
                      <Typography variant='caption' color='text.secondary'>{row.quoteAsPer ?? ''}</Typography>
                    </TableCell>
                    <TableCell align='right' sx={{ whiteSpace: 'nowrap' }}>{formatAmount(row.quoteValue)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant='body2'>{row.workOrderNo}</Typography>
                      <Typography variant='caption' color='text.secondary'>
                        {row.workOrderDate ? `${formatDate(row.workOrderDate)} · ` : ''}by {row.workOrderByName ?? '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align='center'>
                      <Tooltip title='Read the quotation letter'>
                        <span>
                          <IconButton size='small' color='primary' disabled={opening !== null} onClick={() => view(row)}
                            aria-label={`Quotation letter for ${row.jobCode}`}>
                            {opening === row.quoteId ? <CircularProgress size={18} /> : <Icon icon='mdi:file-pdf-box' width={20} />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align='right'>
                      <Button size='small' variant='contained' disabled={approving !== null} onClick={() => approve(row)}>
                        {approving === row.quoteId ? 'Approving…' : 'Approve'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Card>

      <QuotationPdfViewer
        file={viewing?.file ?? null}
        title={viewing ? `Quotation · ${viewing.row.jobCode} · ${viewing.row.jobName}` : ''}
        onClose={() => {
          release();
          setViewing(null);
        }}
      />
    </Stack>
  );
}
